import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PencilSimple, Plus, Trash } from '@phosphor-icons/react';

import {
  createFeeRule,
  deleteFeeRule,
  getCategoryFees,
  updateFeeRule,
  type FeeRule,
  type FeeRuleInput,
} from '@/api/notices';
import type { TeamCategory } from '@/api/teamCategories';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useConfirm } from '@/components/ConfirmDialog';
import { CategoryFeeRow, type CategoryFeeValues } from './CategoryFeeRow';
import { ClubMembershipFeeCard } from './ClubMembershipFeeCard';

// The "Avgifter" tab of the team-category drawer: the everyday fee-authoring
// surface. A club sets the fees every team in the category is billed here —
// the training fee plus any named add-ons (equipment, registration, …), each
// optionally split into age bands. Membership is one club-wide amount set in
// settings and shown read-only. Under the hood these are ordinary fee_rule rows
// in the category's implicit fee profile (level 'section'), which every team in
// the category inherits — but none of that hierarchy leaks into the UI.

export interface CategoryFeesTabProps {
  slug: string;
  category: TeamCategory;
  open: boolean;
  onClose: () => void;
  // Owner/administrator. Gates the club-wide membership editor; the server
  // re-checks on every write.
  canManage: boolean;
}

export function CategoryFeesTab({ slug, category, open, canManage }: CategoryFeesTabProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const qc = useQueryClient();
  const confirm = useConfirm();

  const [editing, setEditing] = useState<FeeRule | 'new' | null>(null);

  const feesQuery = useQuery({
    queryKey: ['category-fees', slug, category.id],
    queryFn: () => getCategoryFees(token, slug, category.id),
    enabled: !!token && open,
  });
  const profileId = feesQuery.data?.fee_profile_id ?? '';
  const rules = useMemo(() => feesQuery.data?.fee_rules ?? [], [feesQuery.data]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['category-fees', slug, category.id] });
    void qc.invalidateQueries({ queryKey: ['notices-config', slug] });
  };

  const saveRuleM = useMutation({
    mutationFn: (vars: { id?: string; body: FeeRuleInput }) =>
      vars.id
        ? updateFeeRule(token, slug, vars.id, vars.body).then(() => ({ id: vars.id! }))
        : createFeeRule(token, slug, vars.body),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  const deleteRuleM = useMutation({
    mutationFn: (id: string) => deleteFeeRule(token, slug, id),
    onSuccess: invalidate,
  });

  // buildBody assembles the fee_rule payload for one category fee: the implicit
  // profile, 'section' level, the amount as a training OR add-on fee, and an
  // optional age band. Everything else stays a wildcard so it applies to the
  // whole category.
  function buildBody(v: CategoryFeeValues): FeeRuleInput {
    const oneTime = v.kind === 'one_time';
    return {
      fee_profile_id: profileId,
      level: 'section',
      section: null,
      team_id: null,
      member_id: null,
      min_age: v.minAge,
      max_age: v.maxAge,
      birth_year: null,
      role: null,
      member_type: null,
      tryout_only: null,
      membership_minor: null,
      training_minor: oneTime ? null : v.amountMinor,
      addon_minor: oneTime ? v.amountMinor : null,
      label: v.label,
      exempt_membership: false,
      exempt_training: false,
      period: v.period,
      reason: null,
      note: null,
      currency: null,
    };
  }

  async function onDelete(rule: FeeRule) {
    const ok = await confirm({
      title: t('notices.fees.deleteRuleTitle', { defaultValue: 'Delete fee?' }),
      body: t('notices.fees.deleteRuleBody', { defaultValue: 'This archives the fee.' }),
      confirmLabel: t('common.delete'),
      tone: 'destructive',
    });
    if (ok) deleteRuleM.mutate(rule.id);
  }

  const isEditingRow = (r: FeeRule) => editing !== 'new' && editing !== null && editing.id === r.id;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4 sm:p-6">
      {/* Club membership — one amount for the entire club, editable right here
          (it used to sit on the categories page, away from the fees it belongs
          with). The card carries its own "whole club" framing + confirm step. */}
      <ClubMembershipFeeCard slug={slug} canManage={canManage} categoryName={category.name} />

      {/* Training fees for this category. */}
      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          {/* Named heading, not a bare "Fees": directly under a card labelled
              "whole club", the contrast is what tells the admin at a glance
              which of the two is scoped to this category. */}
          <h3 className="font-heading text-sm font-semibold">
            {t('notices.fees.categoryFeesTitleNamed', {
              defaultValue: 'Fees for {{category}}',
              category: category.name,
            })}
          </h3>
          <Button
            size="sm"
            className="gap-1"
            disabled={editing !== null || !profileId}
            onClick={() => setEditing('new')}
          >
            <Plus size={16} />
            {t('notices.fees.addFee', { defaultValue: 'Add fee' })}
          </Button>
        </div>

        {feesQuery.isPending ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : rules.length === 0 && editing !== 'new' ? (
          <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {t('notices.fees.emptyCategoryFees', {
              defaultValue: 'No fees yet. Add one — every team in this category will be billed it.',
            })}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {rules.map((rule) =>
              isEditingRow(rule) ? (
                <CategoryFeeRow
                  key={rule.id}
                  rule={rule}
                  saving={saveRuleM.isPending}
                  onCancel={() => setEditing(null)}
                  onSave={(vals) => saveRuleM.mutate({ id: rule.id, body: buildBody(vals) })}
                />
              ) : (
                <div
                  key={rule.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{feeName(t, rule)}</span>
                    <span className="tabular-nums">{feeAmount(rule)}</span>
                    <Badge variant="secondary">{ageBandLabel(t, rule)}</Badge>
                    {rule.period && (
                      <span className="text-sm text-muted-foreground">
                        · {t(`notices.period.${rule.period}`, { defaultValue: rule.period })}
                      </span>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={t('common.edit')}
                      disabled={editing !== null}
                      onClick={() => setEditing(rule)}
                    >
                      <PencilSimple size={16} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={t('common.delete')}
                      disabled={editing !== null}
                      onClick={() => void onDelete(rule)}
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              ),
            )}
            {editing === 'new' && (
              <CategoryFeeRow
                key="new"
                rule={null}
                saving={saveRuleM.isPending}
                onCancel={() => setEditing(null)}
                onSave={(vals) => saveRuleM.mutate({ body: buildBody(vals) })}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// feeName renders a rule's own name, falling back to its type for legacy rows
// that predate free-text naming.
function feeName(t: ReturnType<typeof useTranslation>['t'], r: FeeRule): string {
  if (r.label) return r.label;
  return r.addon_minor != null
    ? t('notices.fees.kind.oneTime', { defaultValue: 'One-time fee' })
    : t('notices.fees.kind.recurring', { defaultValue: 'Recurring (training fee)' });
}

// feeAmount renders the rule's amount (training or add-on) in kr.
function feeAmount(r: FeeRule): string {
  const minor = r.addon_minor ?? r.training_minor;
  return minor != null ? `${(minor / 100).toLocaleString('sv-SE')} kr` : '—';
}

// ageBandLabel renders a rule's optional age band as a short human phrase.
function ageBandLabel(
  t: ReturnType<typeof useTranslation>['t'],
  r: FeeRule,
): string {
  const lo = r.min_age ?? null;
  const hi = r.max_age ?? null;
  if (lo == null && hi == null) return t('notices.fees.allAges', { defaultValue: 'All ages' });
  if (lo != null && hi != null)
    return t('notices.fees.ageBand', { defaultValue: 'ages {{lo}}–{{hi}}', lo, hi });
  if (lo != null) return t('notices.fees.ageFrom', { defaultValue: 'ages {{lo}}+', lo });
  return t('notices.fees.ageTo', { defaultValue: 'up to age {{hi}}', hi });
}
